import { Range } from "@shopify/polaris";

import { defaultValues, defaultValuesV2 } from "./config";
import { TRACKING_SCOPES } from "./constants";
import { StoreType } from "./components/types";

const eventCategoriesArray = [
  TRACKING_SCOPES.productView,
  TRACKING_SCOPES.login,
  TRACKING_SCOPES.logout,
];

const webhookCategoriesArray = [
  TRACKING_SCOPES.order,
  TRACKING_SCOPES.shipping,
  TRACKING_SCOPES.customer,
];

const productTrackingArray = [TRACKING_SCOPES.catalog];

export interface Data {
  id: string;
  trackingScopes: string[];
  createdAt: string;
  iterableKey: string;
  events_count: { [key: string]: number };
}

export interface DataV2 {
  iterable_api_key: string;
  is_tracking_catalog: boolean;
  catalog_access: boolean;
  store_type: StoreType;
  webhook_event_topics: string[];
  custom_event_topics: string[];
  resync_catalog_from?: string | null;
  is_merchant_using_v2?: boolean;
}

export interface FormValues {
  iterableApiKey: string;
  eventCategories: string[];
  webhookCategories: string[];
  productTracking: string[];
  storeType: StoreType;
}

export interface FormValuesV2 {
  iterableApiKey: string;
  clientSideEvents: string[];
  webhookTopics: string[];
  catalogResyncing: boolean;
  catalogAccess: boolean;
  storeType: string;
  resyncCatalogFrom: string;
}

export const sortAlphabetically = (arr: Array<string>) =>
  arr.sort((a, b) => a.localeCompare(b));

export const isFormValues = (value: any): value is Data => {
  return (
    "id" in value &&
    "iterableKey" in value &&
    "trackingScopes" in value &&
    "createdAt" in value
  );
};

export const isFormValuesV2 = (value: any): value is DataV2 => {
  return (
    "iterable_api_key" in value &&
    "store_type" in value &&
    "webhook_event_topics" in value &&
    "is_tracking_catalog" in value
  );
};

export interface RequestPayload {
  iterableKey?: string;
  trackingScopes: string[];
  syncCatalogFrom?: string;
  storeType?: string;
}

type CheckboxValues = Omit<FormValues, "iterableApiKey">;

export const getInitialValues = (data: Data): FormValues => {
  const checkboxValues: CheckboxValues = data?.trackingScopes?.reduce(
    (acc: CheckboxValues, el: string): CheckboxValues => {
      if (eventCategoriesArray.includes(el)) {
        return {
          ...acc,
          eventCategories: sortAlphabetically([...acc.eventCategories, el]),
        };
      }
      if (webhookCategoriesArray.includes(el)) {
        return {
          ...acc,
          webhookCategories: sortAlphabetically([...acc.webhookCategories, el]),
        };
      }
      if (productTrackingArray.includes(el)) {
        return {
          ...acc,
          productTracking: sortAlphabetically([...acc.productTracking, el]),
        };
      }
      return acc;
    },
    {
      eventCategories: defaultValues.eventCategories,
      webhookCategories: defaultValues.webhookCategories,
      productTracking: defaultValues.productTracking,
    } as CheckboxValues
  );

  const { eventCategories, webhookCategories, productTracking } =
    checkboxValues;

  return {
    iterableApiKey: data?.iterableKey || defaultValues.iterableApiKey,
    eventCategories,
    webhookCategories,
    productTracking,
    storeType: defaultValues.storeType,
  };
};

export const getInitialValuesV2 = (data: DataV2): FormValuesV2 => {
  return {
    iterableApiKey: data?.iterable_api_key || defaultValuesV2.iterableApiKey,
    clientSideEvents: data?.custom_event_topics || defaultValuesV2.clientSideEvents,
    webhookTopics: data?.webhook_event_topics || defaultValuesV2.webhookTopics,
    catalogResyncing:
      data?.is_tracking_catalog ?? defaultValuesV2.catalogResyncing,
    catalogAccess: data?.catalog_access ?? defaultValuesV2.catalogAccess,
    storeType: data?.store_type || defaultValuesV2.storeType,
    resyncCatalogFrom:
      data?.resync_catalog_from ?? defaultValuesV2.resyncCatalogFrom,
  };
};

export const getRequestPayload = (
  formValues: FormValues,
  initialValues: FormValues,
  selectedDate: Range
): RequestPayload => {
  const {
    iterableApiKey,
    eventCategories = [],
    webhookCategories = [],
    productTracking = [],
  } = formValues;

  // TODO: consider to do not send trackingScopes when they are not changed
  let res: RequestPayload = {
    trackingScopes: [
      ...eventCategories,
      ...webhookCategories,
      ...productTracking,
    ].filter((value: string): boolean => value !== "hidden"),
  };

  if (iterableApiKey !== initialValues.iterableApiKey) {
    res = { ...res, iterableKey: iterableApiKey };
  }

  if (productTracking?.includes(TRACKING_SCOPES.catalog)) {
    res = { ...res, syncCatalogFrom: selectedDate?.start?.toISOString() };
  }

  return res;
};
